import React from 'react';
import StorefrontLayout from '../components/layout/StorefrontLayout';
import { useTranslation } from 'react-i18next';
import { applyContentLocaleToUrl } from '../utils/contentLocaleQuery';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type SiteContent = {
  termsTitle: string;
  termsBody: string;
  privacyTitle: string;
  privacyBody: string;
  cookiesTitle: string;
  cookiesBody: string;
  paymentSecurityTitle: string;
  paymentSecurityBody: string;
  termsUpdatedAt?: string | null;
};

function renderTextWithLinks(text: string) {
  // Basic URL linkification for legal content.
  const urlRegex = /(https?:\/\/[^\s)]+)|(\bwww\.[^\s)]+)/gi;
  const parts: Array<React.ReactNode> = [];
  let lastIndex = 0;
  const raw = String(text || '');
  raw.replace(urlRegex, (match, _p1, _p2, offset) => {
    const start = offset ?? 0;
    if (start > lastIndex) parts.push(raw.slice(lastIndex, start));
    const href = match.startsWith('http') ? match : `https://${match}`;
    parts.push(
      <a
        key={`${href}:${start}`}
        href={href}
        target="_blank"
        rel="noreferrer"
        className="font-semibold text-emerald-800 underline underline-offset-2 hover:text-emerald-900"
      >
        {match}
      </a>,
    );
    lastIndex = start + match.length;
    return match;
  });
  if (lastIndex < raw.length) parts.push(raw.slice(lastIndex));
  return <>{parts}</>;
}

function LegalBody({ body }: { body: string }) {
  const lines = String(body || '').split('\n');
  const blocks: Array<{ type: 'h2' | 'h3' | 'p' | 'ul'; text?: string; items?: string[] }> = [];

  const pushParagraph = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    blocks.push({ type: 'p', text: trimmed });
  };

  let pendingPara: string[] = [];
  let pendingList: string[] = [];

  const flushPara = () => {
    if (!pendingPara.length) return;
    pushParagraph(pendingPara.join(' ').replace(/\s+/g, ' ').trim());
    pendingPara = [];
  };
  const flushList = () => {
    if (!pendingList.length) return;
    blocks.push({ type: 'ul', items: [...pendingList] });
    pendingList = [];
  };

  for (const rawLine of lines) {
    const line = rawLine.replace(/\r/g, '');
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      flushPara();
      continue;
    }

    if (trimmed.startsWith('## ')) {
      flushList();
      flushPara();
      blocks.push({ type: 'h2', text: trimmed.slice(3).trim() });
      continue;
    }

    if (trimmed.startsWith('### ')) {
      flushList();
      flushPara();
      blocks.push({ type: 'h3', text: trimmed.slice(4).trim() });
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      flushList();
      flushPara();
      blocks.push({ type: 'h3', text: trimmed });
      continue;
    }

    if (trimmed.startsWith('- ')) {
      flushPara();
      pendingList.push(trimmed.slice(2).trim());
      continue;
    }

    pendingPara.push(trimmed);
  }

  flushList();
  flushPara();

  return (
    <div className="mt-4 space-y-4 text-sm leading-relaxed text-slate-700">
      {blocks.map((b, idx) => {
        if (b.type === 'h2') {
          return (
            <h3 key={idx} className="pt-2 text-lg font-bold tracking-tight text-slate-900">
              {b.text}
            </h3>
          );
        }
        if (b.type === 'h3') {
          return (
            <h4 key={idx} className="pt-1 text-base font-semibold text-slate-900">
              {b.text}
            </h4>
          );
        }
        if (b.type === 'ul') {
          return (
            <ul key={idx} className="list-disc space-y-1 pl-5 text-slate-700">
              {(b.items || []).map((it, j) => (
                <li key={`${idx}:${j}`}>{renderTextWithLinks(it)}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={idx} className="text-slate-700">
            {renderTextWithLinks(b.text || '')}
          </p>
        );
      })}
    </div>
  );
}

export default function LegalPage() {
  const [content, setContent] = React.useState<SiteContent | null>(null);
  const { t, i18n } = useTranslation();

  const formatLegalDate = (raw?: string | null) => {
    if (!raw) return '';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(i18n.language, { year: 'numeric', month: 'short', day: '2-digit' });
  };

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(
          applyContentLocaleToUrl(
            `${API_BASE}/public/site-content`,
            i18n.resolvedLanguage || i18n.language,
          ),
        );
        if (!res.ok) return;
        const data = (await res.json()) as SiteContent;
        if (!cancelled) setContent(data);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [i18n.language, i18n.resolvedLanguage]);

  return (
    <StorefrontLayout contentContained>
      <section className="space-y-6 py-6">
        <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            {content?.termsTitle || t('footer.terms')}
          </h1>
          {content?.termsUpdatedAt ? (
            <p className="mt-2 text-xs text-slate-500">
              {t('storefront.legal_last_updated')}: {formatLegalDate(content.termsUpdatedAt)}
            </p>
          ) : null}
          <LegalBody body={content?.termsBody || ''} />
        </article>

        <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            {content?.privacyTitle || t('footer.privacy')}
          </h2>
          <LegalBody body={content?.privacyBody || ''} />
        </article>

        <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            {content?.cookiesTitle || t('storefront.cookies_policy_title')}
          </h2>
          <LegalBody body={content?.cookiesBody || ''} />
        </article>

        <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            {content?.paymentSecurityTitle || t('storefront.payment_security_title')}
          </h2>
          <LegalBody body={content?.paymentSecurityBody || ''} />
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
            <p>
              {t('storefront.payment_processing_powered_by')}{' '}
              <a
                href="https://stripe.com"
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-slate-900 underline underline-offset-2"
              >
                Stripe
              </a>
              {t('storefront.payment_processing_end')}
            </p>
          </div>
        </article>

        <article
          id="refund-policy"
          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
        >
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            {t('storefront.refund_policy_title', { defaultValue: 'Refund policy' })}
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate-700">
            <p>
              {t('storefront.refund_policy_body_1', {
                defaultValue:
                  'Refund requests are accepted only within configured windows after payment or delivery. Delivered-order requests require an eligible reason and evidence.',
              })}
            </p>
            <p>
              {t('storefront.refund_policy_body_2', {
                defaultValue:
                  'Approved delivered-order refunds may require product return and inspection before payout is processed.',
              })}
            </p>
            <p>
              {t('storefront.refund_policy_body_3', {
                defaultValue:
                  'For customer-caused reasons, return fees may be collected separately and are not automatically deducted from approved refund payouts.',
              })}
            </p>
          </div>
        </article>
      </section>
    </StorefrontLayout>
  );
}

